import React, { useRef, useState } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { AGGREGATE_NAMES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { useAddConsorsNoteMutation } from './hooks/useAddConsorsNoteMutation';
import Spinner from '~/components/icons/Spinner';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useUser from '~/hooks/useUser';
import { cn } from '~/utils/cnTailwind';

export const CreateNote: React.FC = () => {
    const { t } = useTranslation();
    const user = useUser();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [charCount, setCharCount] = useState(0);
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { isPending, mutate: addConsorsNote } = useAddConsorsNoteMutation(leadId);
    const isPermissionDenied = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].addConsorsNote;
    const maxLength = 500;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        if (value.length <= maxLength) {
            setCharCount(value.length);
        } else {
            e.target.value = value.slice(0, maxLength);
            setCharCount(maxLength);
        }
    };

    const handleSave = () => {
        const noteContent = textareaRef.current?.value || '';
        addConsorsNote({
            content: noteContent,
            creationDate: new Date().toISOString(),
            employeeName: `${user.firstName} ${user.lastName}`,
            id: uuid(),
        });
        if (textareaRef.current) {
            textareaRef.current.value = '';
        }
        setCharCount(0);
    };

    return (
        <>
            <div className="text-grey text-xs font-bold mb-4">{t('CONSORS_NOTES.NOTE_HEADER')}</div>
            <div className="w-full max-w-3xl mx-auto p-2 bg-white">
                <textarea
                    ref={textareaRef}
                    className="w-full min-h-[100px] resize-y border border-blue rounded-md focus:outline-none focus:border-2 font-sans text-sm p-2 bg-white"
                    placeholder={t('CONSORS_NOTES.NOTE_PLACEHOLDER')}
                    onChange={handleChange}
                />
                <div className="flex justify-end items-center space-x-4 mt-2">
                    <div className="text-xs text-gray-500">
                        {charCount} / {maxLength} {t('CONSORS_NOTES.CHARACTERS')}
                    </div>
                    <button
                        onClick={handleSave}
                        className={cn(
                            'border border-grey text-grey hover:border-blue hover:bg-blue hover:text-white font-sans text-sm py-2 px-4 rounded w-28 transition-colors',
                            (isPending || charCount === 0 || isPermissionDenied) && 'opacity-50 cursor-not-allowed',
                        )}
                        disabled={isPending || charCount === 0 || isPermissionDenied}
                    >
                        <span className="flex items-center justify-center h-5">
                            {isPending ? <Spinner className="w-5 h-5" /> : t('CONSORS_NOTES.SEND')}
                        </span>
                    </button>
                </div>
            </div>
        </>
    );
};
